import React, { useState } from "react";

import {
  faPlus,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { Cell, ColumnDef } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { startsWithInternalPrefix } from "src/api/constants";
import { ApiKeyActions } from "src/apiKeys/ApiKeyActions";
import { useWorkspaceApiKeys } from "src/apiKeys/api/queries";
import { CreateApiKeyModal } from "src/apiKeys/CreateApiKeyModal";
import { DeleteApiKeyModal } from "src/apiKeys/DeleteApiKeyModal";
import { EditApiKeyModal } from "src/apiKeys/EditApiKeyModal";
import { KeyType } from "src/apiKeys/KeyType";
import { ShowApiKeyModal } from "src/apiKeys/ShowApiKeyModal";
import EmptyCube from "src/assets/empty-state-cube.svg?react";
import { Card } from "src/base-components/Card";
import { Pill } from "src/base-components/Pill";
import { TableComp as Table } from "src/base-components/Table";
import {
  ApiKeyType,
  WorkspaceServiceAccount,
  WorkspaceServiceAccountWithKey,
} from "src/clients/taktile-api";
import { RawCell } from "src/dataTable/RawCell";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { ColumnHeader } from "src/jobs/common/TableElements";
import { DashboardContent } from "src/layout/DashboardContent";
import { dateFromNow, formatDate } from "src/utils/datetime";
import { errorMessage } from "src/utils/stringUtils";

const renderDateInfo = <T extends WorkspaceServiceAccount>(
  cell: Cell<T, string>,
) => {
  const isoDate = cell.getValue();
  const fromNow = isoDate ? dateFromNow(isoDate) : "Never";
  const detailDate = isoDate
    ? formatDate(isoDate, "dd MMM yy HH:mm:ss")
    : "Never used";

  return (
    <Tooltip placement="top" title={detailDate}>
      <RawCell<ApiKeyType>
        cell={<span>{fromNow}</span>}
        classNameOverrides="min-h-[48px]"
      />
    </Tooltip>
  );
};

const renderNoApiKeys = () => (
  <div className="mx-auto flex h-[19.875rem] w-[57.25rem] flex-row items-center justify-center">
    <div className="flex flex-row">
      <EmptyCube className="mr-5" />
      <div className="text-gray-800">
        <div className="pt-1.5 font-inter-semibold-14px">No API keys found</div>
        <div className="font-inter-normal-13px">
          Adjust your filters or create an API key to integrate <br /> Taktile
          with your system.
        </div>
      </div>
    </div>
  </div>
);

const Header: React.FC<{ header?: string }> = ({ header }) => (
  <ColumnHeader>{header}</ColumnHeader>
);

type Props = {
  organizationId: string;
  workspaceId: string;
};

type CallbackWithApiKey = (apiKey: WorkspaceServiceAccount) => void;

const buildColumns = (
  onEdit: CallbackWithApiKey,
  onDelete: CallbackWithApiKey,
): ColumnDef<WorkspaceServiceAccount>[] => {
  const columns: ColumnDef<WorkspaceServiceAccount>[] = [
    {
      id: "keyName",
      header: () => <Header header="Key name" />,
      accessorFn: (row) => row.name,
      cell: ({ cell }) => {
        const isInternalKey = startsWithInternalPrefix(cell.row.original.name);
        const wrapper = (
          <span className="inline-flex items-center gap-2">
            {isInternalKey && (
              <Pill size="sm" variant="red">
                <Pill.Text>Internal</Pill.Text>
              </Pill>
            )}
            {cell.renderValue<string>()}
          </span>
        );
        return (
          <RawCell<WorkspaceServiceAccount>
            cell={wrapper}
            classNameOverrides="min-h-[48px] max-w-0"
          />
        );
      },
    },
    {
      id: "keyTypes",
      header: () => <Header header="Key permissions" />,
      accessorFn: (row) => row.key_types,
      cell: ({ cell }) => <KeyType value={cell.getValue() as ApiKeyType[]} />,
    },
    {
      id: "createdBy",
      header: () => <Header header="Created by" />,
      accessorFn: (row) => row.sponsored_by_username ?? "",
      cell: ({ cell }) => {
        const isDeactivated =
          cell.row.original.sponsored_by_is_active === false;

        return (
          <div className="inline-flex min-h-[48px] items-center gap-2">
            <span
              className={twJoin(
                "font-inter-medium-12px",
                isDeactivated && "text-gray-500",
              )}
            >
              {cell.getValue<string>()}
            </span>
            {isDeactivated && (
              <Pill
                fontType="text"
                dataLoc="creator-deactivated"
                size="sm"
                variant="gray"
              >
                <Pill.Text>Deactivated</Pill.Text>
              </Pill>
            )}
          </div>
        );
      },
    },
    {
      id: "createdAt",
      header: () => <Header header="Created at" />,
      accessorFn: (row) => row.created_at,
      cell: ({ cell }) =>
        renderDateInfo(cell as Cell<WorkspaceServiceAccount, string>),
    },
    {
      id: "lastUsed",
      header: () => <Header header="Last used" />,
      accessorFn: (row) => row.last_used_at ?? "",
      cell: ({ cell }) =>
        renderDateInfo(cell as Cell<WorkspaceServiceAccount, string>),
    },
    {
      id: "actions",
      header: () => <Header />,
      cell: (cell) => {
        const apiKey = cell.row.original;
        return (
          <ApiKeyActions
            onDeleteClick={() => onDelete(apiKey)}
            onEditClick={() => onEdit(apiKey)}
          />
        );
      },
    },
  ];

  return columns;
};

export const ApiKeyOverview: React.FC<Props> = ({
  organizationId,
  workspaceId,
}) => {
  const { apiKeys } = useCapabilities();
  const { isLoading, data, isError, error } = useWorkspaceApiKeys(
    organizationId,
    workspaceId,
  );

  const editApiKeyModal = useModal<WorkspaceServiceAccount>();
  const deleteApiKeyModal = useModal<WorkspaceServiceAccount>();
  const createApiKeyModal = useModal();
  const showApiKeyModal = useModal<WorkspaceServiceAccountWithKey>();
  const [creatorFilter, setCreatorFilter] = useState<
    "active" | "deactivated" | null
  >(null);
  const columns = buildColumns(
    editApiKeyModal.openModal,
    deleteApiKeyModal.openModal,
  );

  const filteredData =
    creatorFilter == null || !data
      ? data
      : data.filter((key) => {
          if (creatorFilter === "active")
            return key.sponsored_by_is_active !== false;
          return key.sponsored_by_is_active === false;
        });

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          dataLoc="api-keys-error-state"
          description={errorMessage(error)}
          headline="Error loading API keys"
          icon={faTriangleExclamation}
          variant="error"
        />
      );
    }

    if (!isLoading && filteredData?.length === 0) {
      return renderNoApiKeys();
    }

    return (
      <Table
        columns={columns}
        data={filteredData ?? []}
        isLoading={isLoading}
      />
    );
  };

  return (
    <DashboardContent
      Header={
        <SubHeader title="API keys">
          <SubHeaderFilter
            dataLoc="filter-api-keys-creator"
            options={[
              { key: "active" as const, value: "Created by active user" },
              {
                key: "deactivated" as const,
                value: "Created by deactivated user",
              },
            ]}
            selected={creatorFilter}
            onChange={setCreatorFilter}
          />
          {apiKeys.canAccess && (
            <SubHeader.Button
              icon={faPlus}
              tooltip="Create API key"
              onClick={() => createApiKeyModal.openModal()}
            />
          )}
        </SubHeader>
      }
    >
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          {renderContent()}
        </Card.Content>
      </Card>
      <EditApiKeyModal
        afterLeave={editApiKeyModal.afterLeave}
        keyToEdit={editApiKeyModal.data}
        open={editApiKeyModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={editApiKeyModal.closeModal}
      />
      <DeleteApiKeyModal
        afterLeave={deleteApiKeyModal.afterLeave}
        keyToDelete={deleteApiKeyModal.data}
        open={deleteApiKeyModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={deleteApiKeyModal.closeModal}
      />
      <CreateApiKeyModal
        open={createApiKeyModal.isOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onApiKeyCreate={showApiKeyModal.openModal}
        onClose={createApiKeyModal.closeModal}
      />
      <ShowApiKeyModal
        afterLeave={showApiKeyModal.afterLeave}
        createdApiKey={showApiKeyModal.data}
        open={showApiKeyModal.isOpen}
        onClose={showApiKeyModal.closeModal}
      />
    </DashboardContent>
  );
};
